import {
  createContext,
  useContext,
  ReactNode,
  useEffect,
  useMemo,
  useCallback,
  useRef,
  useState,
} from "react";
import { Session, User, AuthError } from "@supabase/supabase-js";
import { useAuthState } from "@/hooks/auth/useAuthState";
import { useFetchUserProfile } from "@/hooks/auth/useFetchUserProfile";
import { useAuthMethods } from "@/hooks/auth/useAuthMethods";
import { useAdminStatus } from "@/hooks/auth/useAdminStatus";
import { useUserSessionManager } from "@/hooks/auth/useUserSessionManager";
import { Tables, TablesUpdate } from "@/types/database.types";
import { redirectService } from "@/services/redirectService";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { setUser } from "@/config/sentry";
import { analytics } from "@/hooks/useAnalytics";

// Type-safe utility to get safe organization fields for updates
type OrganizationUpdateFields = TablesUpdate<"organizations">;
type SensitiveOrganizationFields =
  | "stripe_customer_id"
  | "stripe_subscription_id"
  | "subscription_status"
  | "is_super_admin"
  | "current_period_end"
  | "active_agents_count"
  | "conversations_this_month"
  | "conversations_reset_date"
  | "files_count"
  | "knowledge_items_count"
  | "storage_usage_mb"
  | "plan_id";

type SafeOrganizationUpdateFields = Omit<
  OrganizationUpdateFields,
  SensitiveOrganizationFields
>;

// Integrated organization functionality from OrganizationContext
export type Organization = Tables<"organizations">;

type AuthContextType = {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  signIn: (
    email: string,
    password: string,
    rememberMe?: boolean,
  ) => Promise<{
    error: AuthError | Error | null;
    redirectTo: string | null;
  }>;
  signUp: (
    email: string,
    password: string,
    organizationName?: string,
    fbclid?: string | null,
  ) => Promise<{
    error: AuthError | Error | null;
  }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{
    error: AuthError | Error | null;
  }>;
  updatePassword: (password: string) => Promise<{
    error: AuthError | Error | null;
  }>;
  userProfile: Tables<"profiles"> | null;
  organization: Tables<"organizations"> | null;
  isProfileLoading: boolean;
  refreshProfile: () => void;
  updateOrganization?: (
    data: Partial<Tables<"organizations">>,
  ) => Promise<void>;
  isAdmin?: boolean;
  isAdminLoading?: boolean;
  refreshAdminStatus?: () => void;
  isWaitingForOrganization: boolean;
  inRecovery: boolean;

  // Added from OrganizationContext
  agents?: Array<{ id: string; agent_name: string; title?: string }>;
  integrationConnections?: Array<{
    id: string;
    tool: string;
    connection_id: string;
    is_active: boolean;
    connection_data?: any;
  }>;
  refreshOrganization: () => Promise<void>;
  refreshIntegrationConnections: () => Promise<void>;
  hasPermission: (feature: string) => boolean;
  isFeatureEnabled: (feature: string) => boolean;
  isSubscriptionPaused: () => boolean;
  getUsageStats: () => {
    agents: number;
    conversations: number;
    storage: number;
    files: number;
    knowledgeItems: number;
  };
  subscribeToUpdates: (callback: (org: Organization) => void) => () => void;
  lastOrganizationUpdated?: number | null;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  const {
    user,
    session,
    isLoading: isAuthLoading,
    setIsLoading,
    lastAuthEvent,
    inRecovery,
  } = useAuthState();
  const {
    userProfile,
    organization,
    isLoading: isProfileLoading,
    refreshProfile,
  } = useFetchUserProfile(user);

  const {
    signIn,
    signUp,
    signOut: originalSignOut,
    resetPassword,
    updatePassword,
  } = useAuthMethods(setIsLoading);
  const {
    isAdmin,
    isLoading: isAdminLoading,
    refreshAdminStatus,
  } = useAdminStatus();

  // Include both auth and profile loading in the main loading state
  const isLoading = isAuthLoading || isProfileLoading;

  // Added from OrganizationContext
  const [agents, setAgents] = useState<
    Array<{ id: string; agent_name: string; title?: string }>
  >([]);
  const [integrationConnections, setIntegrationConnections] = useState<
    Array<{
      id: string;
      tool: string;
      connection_id: string;
      is_active: boolean;
      connection_data?: any;
    }>
  >([]);
  const [lastOrganizationUpdated, setLastOrganizationUpdated] = useState<
    number | null
  >(null);
  const realtimeChannelRef = useRef<any>(null);
  const mountedRef = useRef(true);

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (realtimeChannelRef.current) {
        supabase.removeChannel(realtimeChannelRef.current);
        realtimeChannelRef.current = null;
      }
    };
  }, []);

  // Update Sentry user context and Segment analytics when user/profile changes
  useEffect(() => {
    if (user && userProfile) {
      setUser({
        id: user.id,
        email: user.email || undefined,
        username: userProfile.full_name || undefined,
      });

      // Comprehensive user identification in Segment with organization data
      analytics.identifyUser(
        {
          id: user.id,
          email: user.email,
          user_metadata: user.user_metadata,
        },
        organization || undefined,
        {
          firstName: userProfile.first_name || undefined,
          lastName: userProfile.last_name || undefined,
        },
      );
    } else if (!user) {
      // Clear user context on logout
      setUser({ id: "", email: undefined, username: undefined });
      // Reset analytics
      analytics.reset();
    }
  }, [user, userProfile, organization]);

  // Initialize session manager
  const sessionManager = useUserSessionManager({
    user,
    organization,
    isLoading,
  });

  // Enhanced signOut that includes session cleanup
  const signOut = useCallback(async () => {
    try {
      // First perform our comprehensive cleanup
      await sessionManager.performLogout();

      // Then perform the original signOut
      await originalSignOut();
    } catch (error) {
      console.error("[AuthContext] Error during enhanced logout:", error);

      // Still attempt the original signOut even if cleanup fails
      try {
        await originalSignOut();
      } catch (signOutError) {
        console.error(
          "[AuthContext] Original signOut also failed:",
          signOutError,
        );
      }

      toast({
        variant: "destructive",
        title: "Logout Warning",
        description:
          "Logout completed but some data may not have been cleared completely.",
        duration: 5000,
      });
    }
  }, [sessionManager, originalSignOut, toast]);

  // Track if we're waiting for organization data after registration
  const [isWaitingForOrganization, setIsWaitingForOrganization] =
    useState(false);

  // Check for pending new user redirect and update waiting state
  useEffect(() => {
    const pendingNewUserRedirect = sessionStorage.getItem(
      "pendingNewUserRedirect",
    );

    if (pendingNewUserRedirect === "true") {
      if (user && !organization && !isProfileLoading) {
        setIsWaitingForOrganization(true);

        // Set a longer timeout since organization creation can take time
        const timeoutId = setTimeout(() => {
          // console.warn(
          //   "[AuthContext] Timeout waiting for organization data, proceeding anyway"
          // );
          setIsWaitingForOrganization(false);
          sessionStorage.removeItem("pendingNewUserRedirect");
        }, 15000); // 15 second timeout

        return () => clearTimeout(timeoutId);
      } else if (user && organization) {
        setIsWaitingForOrganization(false);
        // Clear the flag since we now have the organization
        sessionStorage.removeItem("pendingNewUserRedirect");
        // Force a refresh of the profile query to stop polling
        refreshProfile();
      }
    }
  }, [user, organization, isProfileLoading, refreshProfile]);

  // Implement updateOrganization function
  const updateOrganization = useCallback(
    async (data: Partial<Tables<"organizations">>) => {
      if (!organization?.id) {
        throw new Error("No organization found to update");
      }

      try {
        // Type-safe field filtering - only include fields that are safe to update
        const safeData: Partial<SafeOrganizationUpdateFields> = {};

        // Define sensitive fields that should never be updated through this function
        const sensitiveFields: Array<keyof Tables<"organizations">> = [
          "stripe_customer_id",
          "stripe_subscription_id",
          "subscription_status",
          "is_super_admin",
          "current_period_end",
          "active_agents_count",
          "conversations_this_month",
          "conversations_reset_date",
          "files_count",
          "knowledge_items_count",
          "storage_usage_mb",
          "plan_id",
        ];

        // Filter out sensitive fields and only include safe ones
        Object.entries(data).forEach(([key, value]) => {
          const fieldKey = key as keyof Tables<"organizations">;
          if (!sensitiveFields.includes(fieldKey) && value !== undefined) {
            (safeData as Record<string, unknown>)[fieldKey] = value;
          }
        });

        // Ensure we have some data to update
        if (Object.keys(safeData).length === 0) {
          throw new Error("No valid fields provided for update");
        }

        const { error } = await supabase
          .from("organizations")
          .update(safeData)
          .eq("id", organization.id!); // Non-null assertion after guard check

        if (error) {
          throw error;
        }

        // Refresh the profile to get the updated organization data
        await refreshProfile();

        // Update last updated timestamp
        setLastOrganizationUpdated(Date.now());

        toast({
          title: "Organization updated",
          description:
            "Your organization settings have been updated successfully.",
        });
      } catch (error) {
        console.error("Error updating organization:", error);
        toast({
          variant: "destructive",
          title: "Update failed",
          description:
            error instanceof Error
              ? error.message
              : "Failed to update organization.",
        });
        throw error;
      }
    },
    [organization, refreshProfile, toast],
  ); // Include full organization object to prevent stale closures

  // Added from OrganizationContext - fetchAgents
  const fetchAgents = useCallback(async (orgId: string) => {
    try {
      const { data, error } = await supabase
        .from("agents")
        .select("id, agent_name, title")
        .eq("organization_id", orgId)
        .eq("archived", false);

      if (error) {
        console.error("Error fetching agents:", error);
        return;
      }

      if (mountedRef.current) {
        setAgents(data || []);
      }
    } catch (error) {
      console.error("Error fetching agents:", error);
    }
  }, []);

  // Fetch integration connections for the organization
  const fetchIntegrationConnections = useCallback(async (orgId: string) => {
    try {
      const { data, error } = await supabase
        .from("integration_connections")
        .select("*")
        .eq("organization_id", orgId)
        .eq("is_active", true);

      if (error) {
        console.error("Error fetching integration connections:", error);
        return;
      }

      if (mountedRef.current) {
        setIntegrationConnections(data || []);
      }
    } catch (error) {
      console.error("Error fetching integration connections:", error);
    }
  }, []);

  // Added from OrganizationContext - refreshOrganization
  const refreshOrganization = useCallback(async (): Promise<void> => {
    if (!organization?.id) {
      return;
    }

    try {
      const { data, error } = await supabase
        .from("organizations")
        .select("*")
        .eq("id", organization.id)
        .single();

      if (error) {
        throw new Error(`Failed to fetch organization: ${error.message}`);
      }

      if (mountedRef.current) {
        // Update the organization data in the profile
        await refreshProfile();

        // Fetch related data
        if (data) {
          await fetchAgents(data.id);
          await fetchIntegrationConnections(data.id);
        }

        // Update last updated timestamp
        setLastOrganizationUpdated(Date.now());
      }
    } catch (error) {
      console.error("Error refreshing organization:", error);
    }
  }, [
    organization?.id,
    refreshProfile,
    fetchAgents,
    fetchIntegrationConnections,
  ]);

  // Refresh integration connections independently
  const refreshIntegrationConnections = useCallback(async (): Promise<void> => {
    if (!organization?.id) {
      return;
    }
    await fetchIntegrationConnections(organization.id);
  }, [organization?.id, fetchIntegrationConnections]);

  // Added from OrganizationContext - hasPermission
  const hasPermission = useCallback(
    (feature: string): boolean => {
      if (!organization) return false;

      // Check if user is super admin
      if (organization.is_super_admin) return true;

      // Check feature flags
      const features = (organization.features as Record<string, boolean>) || {};
      return features[feature] === true;
    },
    [organization],
  );

  // Added from OrganizationContext - isFeatureEnabled
  const isFeatureEnabled = useCallback(
    (feature: string): boolean => {
      if (!organization) return false;

      const features = (organization.features as Record<string, boolean>) || {};
      return features[feature] === true;
    },
    [organization],
  );

  // Check if subscription is paused
  const isSubscriptionPaused = useCallback((): boolean => {
    if (!organization) return false;
    return organization.subscription_status === "paused";
  }, [organization]);

  // Added from OrganizationContext - getUsageStats
  const getUsageStats = useCallback(() => {
    if (!organization) {
      return {
        agents: 0,
        conversations: 0,
        storage: 0,
        files: 0,
        knowledgeItems: 0,
      };
    }

    return {
      agents: organization.active_agents_count || 0,
      conversations: organization.conversations_this_month || 0,
      storage: organization.storage_usage_mb || 0,
      files: organization.files_count || 0,
      knowledgeItems: organization.knowledge_items_count || 0,
    };
  }, [organization]);

  // Added from OrganizationContext - subscribeToUpdates
  const subscribeToUpdates = useCallback(
    (callback: (org: Organization) => void) => {
      if (!organization) {
        return () => {};
      }

      const channel = supabase
        .channel(`org-${organization.id}`)
        .on(
          "postgres_changes",
          {
            event: "UPDATE",
            schema: "public",
            table: "organizations",
            filter: `id=eq.${organization.id}`,
          },
          (payload) => {
            const updatedOrg = payload.new as Organization;
            if (mountedRef.current) {
              // Refresh profile to update the organization data
              refreshProfile();
              setLastOrganizationUpdated(Date.now());
              callback(updatedOrg);
            }
          },
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    },
    [organization, refreshProfile],
  );

  // Set up real-time subscription for organization updates
  useEffect(() => {
    if (!organization?.id) {
      return;
    }

    // Skip real-time updates during onboarding to prevent reloads
    const inOnboardingFlow =
      sessionStorage.getItem("newUserOnboarding") === "true" ||
      sessionStorage.getItem("pendingNewUserRedirect") === "true";

    if (inOnboardingFlow) {
      // console.log(
      //   "[AuthContext] Skipping real-time subscriptions during onboarding",
      // );
      return;
    }

    // Clean up existing channel
    if (realtimeChannelRef.current) {
      supabase.removeChannel(realtimeChannelRef.current);
    }

    // Set up new channel
    realtimeChannelRef.current = supabase
      .channel(`org-updates-${organization.id}`)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "organizations",
          filter: `id=eq.${organization.id}`,
        },
        (payload) => {
          const updatedOrg = payload.new as Organization;
          if (mountedRef.current) {
            // Refresh profile to update the organization data
            refreshProfile();
            setLastOrganizationUpdated(Date.now());
          }
        },
      )
      .subscribe();

    return () => {
      if (realtimeChannelRef.current) {
        supabase.removeChannel(realtimeChannelRef.current);
        realtimeChannelRef.current = null;
      }
    };
  }, [organization?.id, refreshProfile]);

  // Fetch agents and integration connections when organization changes
  useEffect(() => {
    if (organization?.id) {
      fetchAgents(organization.id);
      fetchIntegrationConnections(organization.id);
    }
  }, [organization?.id, fetchAgents, fetchIntegrationConnections]);

  // Debounce redirect checks to prevent race conditions
  const redirectTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  // Auth-related public paths that should redirect logged-in users away
  const authPublicPaths = useMemo(
    () => ["/login", "/register", "/forgot-password", "/reset-password"],
    [],
  );

  // Debounced redirect function to prevent rapid successive calls
  const debouncedRedirectCheck = useCallback(async () => {
    // Clear any existing timeout
    if (redirectTimeoutRef.current) {
      clearTimeout(redirectTimeoutRef.current);
    }

    // Set new timeout for debounced execution
    redirectTimeoutRef.current = setTimeout(async () => {
      try {
        // Skip if still loading or no user
        if (isAuthLoading || isProfileLoading || !user) {
          // console.log(
          //   "[AuthContext] Skipping redirect check - loading or no user:",
          //   {
          //     isAuthLoading,
          //     isProfileLoading,
          //     hasUser: !!user,
          //     currentPath: location.pathname,
          //     timestamp: new Date().toISOString(),
          //   },
          // );
          return;
        }

        // CRITICAL: Skip ALL redirects during password recovery
        if (inRecovery) {
          // console.log(
          //   "[AuthContext] In password recovery mode - blocking all redirects",
          // );
          return;
        }

        // console.log("[AuthContext] REDIRECT CHECK TRIGGERED", {
        //   currentPath: location.pathname,
        //   hasUser: !!user,
        //   userId: user?.id,
        //   lastAuthEvent,
        //   userEmail: user?.email,
        //   inRecovery,
        //   timestamp: new Date().toISOString(),
        // });

        // Check for pending new user redirect from registration
        const pendingNewUserRedirect = sessionStorage.getItem(
          "pendingNewUserRedirect",
        );
        // Also check navigation state for immediate new user detection
        const isNewUserFromState = location.state?.isNewUser === true;
        const isNewUserFromSession = pendingNewUserRedirect === "true";
        const isNewUser = isNewUserFromState || isNewUserFromSession;

        // Add debugging to see what's happening with the flag
        // console.log("[AuthContext] Debug - New user detection:", {
        //   sessionStorageFlag: pendingNewUserRedirect,
        //   navigationState: location.state,
        //   isNewUserFromState,
        //   isNewUserFromSession,
        //   finalIsNewUser: isNewUser,
        // });

        // DON'T clear the pending flag here - wait until after redirect decision
        if (pendingNewUserRedirect || isNewUserFromState) {
          // console.log(
          //   "[AuthContext] Detected new user redirect from registration",
          //   isNewUserFromState
          //     ? "(from navigation state)"
          //     : "(from sessionStorage)",
          // );
        }

        // For new users, we must wait for organization data to be loaded
        if (isNewUser && !organization && !isWaitingForOrganization) {
          // console.log(
          //   "[AuthContext] New user detected but organization not loaded yet, starting wait...",
          // );
          setIsWaitingForOrganization(true);
          return;
        } else if (isNewUser && isWaitingForOrganization) {
          // console.log(
          //   "[AuthContext] Still waiting for new user organization data...",
          // );
          return;
        }

        // Skip redirect logic if we're already on onboarding and it's a new user
        if (isNewUser && location.pathname === "/user-onboarding") {
          // console.log(
          //   "[AuthContext] New user already on onboarding, skipping redirect",
          // );
          // Clear the flags since we're where we need to be
          if (pendingNewUserRedirect) {
            sessionStorage.removeItem("pendingNewUserRedirect");
          }
          return;
        }

        // Include INITIAL_SESSION in allowed auth events to fix refresh behavior
        if (
          !["SIGNED_IN", "USER_UPDATED", "INITIAL_SESSION"].includes(
            lastAuthEvent ?? "",
          ) &&
          !(lastAuthEvent === null && user) &&
          !isNewUser
        ) {
          // console.log(
          //   "[AuthContext] Skipping redirect - not a relevant auth event",
          // );
          return;
        }

        // Skip if no profile yet (still loading)
        if (!userProfile) {
          // console.log("[AuthContext] Skipping redirect - no user profile yet");
          return;
        }

        // FIRST: Check if registration is in progress - if so, skip ALL redirect logic
        const isRegistrationInProgress =
          sessionStorage.getItem("registrationInProgress") === "true";

        if (isRegistrationInProgress) {
          // console.log(
          //   "[AuthContext] Registration in progress, skipping ALL redirect logic to prevent interference",
          // );
          return;
        }

        // console.log("[AuthContext] Checking for post-auth redirect:", {
        //   lastAuthEvent,
        //   pathname: location.pathname,
        //   state: location.state,
        //   organization: organization?.id,
        //   isNewUser,
        //   pendingNewUserRedirect: !!pendingNewUserRedirect,
        // });

        // Extract pathname without query strings for proper path matching
        // React Router's location.pathname already excludes query parameters
        const normalizedPathname = location.pathname;

        // Helper function to normalize paths for comparison (handle trailing slashes)
        const normalizePathForComparison = (path: string) =>
          path.replace(/\/+$/, "") || "/";
        const currentPathNormalized =
          normalizePathForComparison(normalizedPathname);

        const isOnAuthPublicPath = authPublicPaths.some((path) => {
          const pathNormalized = normalizePathForComparison(path);
          return (
            currentPathNormalized === pathNormalized ||
            currentPathNormalized.startsWith(pathNormalized + "/")
          );
        });

        // console.log("[AuthContext] Path analysis:", {
        //   currentPathNormalized,
        //   authPublicPaths,
        //   isOnAuthPublicPath,
        //   location_state: location.state,
        //   timestamp: new Date().toISOString(),
        // });

        // Skip redirect logic if we're on the root path - let DefaultRouteHandler handle it
        if (location.pathname === "/") {
          // console.log(
          //   "[AuthContext] On root path, letting DefaultRouteHandler handle redirect",
          // );
          return;
        }

        // Check for intended destination first (for protected routes)
        const intendedPath = redirectService.getAndClearIntendedDestination();

        if (intendedPath && !isNewUser) {
          // console.log(
          //   "[AuthContext] Redirecting to intended destination:",
          //   intendedPath,
          // );
          navigate(intendedPath, { replace: true });
          return;
        }

        // If we're on an auth-related public page, explicitly marked as coming from auth,
        // or we detected a new user from registration, determine redirect
        // Content-related public paths (like /agentUrl/) should remain accessible to logged-in users
        if (
          isOnAuthPublicPath ||
          location.state?.fromAuth ||
          location.state?.isNewUser ||
          isNewUser
        ) {
          // Skip redirect logic if we're currently on the registration page
          // This prevents interference while registration is handling its own flow
          if (currentPathNormalized === "/register") {
            // console.log(
            //   "[AuthContext] User on registration page, skipping redirect to let registration handle the flow",
            // );
            return;
          }

          // Skip redirect logic if we're on the reset-password page with URL hash parameters
          // This indicates an active password reset flow that should not be interrupted
          if (
            currentPathNormalized === "/reset-password" &&
            window.location.hash.includes("access_token")
          ) {
            // console.log(
            //   "[AuthContext] User on reset-password page with recovery token, skipping redirect to allow password reset completion",
            //   {
            //     hash: window.location.hash.substring(0, 50) + "...",
            //     hasAccessToken: window.location.hash.includes("access_token"),
            //     hasRecoveryType: window.location.hash.includes("type=recovery"),
            //   },
            // );
            return;
          }

          // Determine redirect based on user state
          const decision = await redirectService.getPostAuthRedirect(
            user,
            organization,
            isNewUser || location.state?.isNewUser,
          );

          // console.log("[AuthContext] Redirect decision:", decision);

          // Only redirect if we're not already on the target path (using normalized comparison)
          const targetPathNormalized = normalizePathForComparison(
            decision.path,
          );
          if (currentPathNormalized !== targetPathNormalized) {
            // console.log(
            //   "[AuthContext] Executing redirect from",
            //   currentPathNormalized,
            //   "to",
            //   targetPathNormalized,
            // );

            // Clear the pending flag ONLY after successful redirect decision
            if (pendingNewUserRedirect) {
              sessionStorage.removeItem("pendingNewUserRedirect");
              // console.log(
              //   "[AuthContext] Cleared pendingNewUserRedirect flag after redirect decision",
              // );
            }

            // Clear navigation state by navigating without the state
            if (isNewUserFromState) {
              // console.log(
              //   "[AuthContext] Clearing navigation state after redirect decision",
              // );
            }

            navigate(decision.path, { replace: true });
          } else {
            // console.log(
            //   "[AuthContext] Already on target path, no redirect needed",
            // );

            // Clear the flag even if no redirect needed (already on correct path)
            if (pendingNewUserRedirect) {
              sessionStorage.removeItem("pendingNewUserRedirect");
              // console.log(
              //   "[AuthContext] Cleared pendingNewUserRedirect flag (already on target path)",
              // );
            }

            // Clear navigation state if we're already on the target path
            if (
              isNewUserFromState &&
              currentPathNormalized === "/user-onboarding"
            ) {
              // console.log(
              //   "[AuthContext] Clearing navigation state (already on onboarding)",
              // );
              navigate(decision.path, { replace: true, state: undefined });
            }
          }
        }
      } catch (error) {
        console.error("[AuthContext] Error during redirect check:", error);
        // Don't redirect on error to prevent infinite loops
      }
    }, 300); // Reduced debounce delay since we're now ensuring data is loaded
  }, [
    isAuthLoading,
    isProfileLoading,
    user,
    userProfile,
    organization,
    lastAuthEvent,
    location.pathname,
    location.state,
    navigate,
    authPublicPaths,
    isWaitingForOrganization,
    inRecovery,
  ]);

  // Handle post-auth redirects with debouncing
  useEffect(() => {
    debouncedRedirectCheck();

    // Cleanup timeout on unmount
    return () => {
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current);
      }
    };
  }, [debouncedRedirectCheck]);

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        isLoading,
        signIn,
        signUp,
        signOut,
        resetPassword,
        updatePassword,
        userProfile,
        organization,
        isProfileLoading,
        refreshProfile,
        updateOrganization,
        isAdmin,
        isAdminLoading,
        refreshAdminStatus,
        isWaitingForOrganization,
        inRecovery,
        // Added from OrganizationContext
        agents,
        integrationConnections,
        refreshOrganization,
        refreshIntegrationConnections,
        hasPermission,
        isFeatureEnabled,
        isSubscriptionPaused,
        getUsageStats,
        subscribeToUpdates,
        lastOrganizationUpdated,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
